/**
 * DerivedDataSource — a computed row that derives its cells from another source
 * row addressed by a `DataRef`. Two address modes:
 *
 *   - **cross-row** (`source` attr) — a `DataRef` (an absolute lookupKey, or a
 *     relative path resolved against the nearest keyed ancestor) naming any row;
 *     the derived row's data comes from a row that is *not* its parent. This is
 *     `data-series.source` expressed as a `DataRef`: the consumer holds an
 *     address, not a hardcoded parent link (data-address Phase 2).
 *   - **in-tree** (no `source`) — derive from the parent source row (the
 *     `:debug_dataref` demonstrator).
 *
 * The row is itself addressable — an absolute `lookupKey`, or a relative
 * `localPath` resolved against the nearest keyed ancestor (see
 * `ComputedDataSource.lookupKey`) — so a *derived* series is a real, addressable,
 * renderable row whose cells are *computed from another row*. It reads the
 * resolved source's cells and projects each one by:
 *
 *   - **`field`** — a dotted path into an obj/map cell value
 *     (`"score.hrv_rmssd_milli"`), extracting the leaf number. This is the
 *     Garmin/Whoop case: the source is a `dtype:"obj"` series and the derived
 *     row projects out a scalar (e.g. HRV) that renders as `column.number`.
 *   - **`weights`** — a `{dottedPath: weight}` map; the cell value is the
 *     weighted sum of the named fields *within one source* (e.g. a composite
 *     health score).
 *   - **`blend`** — `[{ source?, field, weight }]`; a weighted sum across
 *     *multiple* sources addressed by `DataRef` (e.g. Whoop HRV + Garmin steps).
 *     The row's primary `source` is the date spine; secondary terms contribute
 *     at matching dates.
 *   - **`factor`** — a scalar multiplier applied to the result (default `1`).
 *
 * With none of these the row passes numeric cells through (× `factor`); an obj
 * cell with no extractable number is *skipped* (sparse series). Read-only by
 * nature (the engine owns the cells; `ComputedDataSource.assertWritable`
 * rejects user writes).
 *
 * Wire type `"derived"`. Design:
 * lona-sdk-2.0.project.docs/content/data-address/adoption.md (Phase 2; the
 * scalar `factor` is the minimal transform — a full formula replaces it once
 * `dataSource:"formula"` is wired).
 */

import type { Option } from "../../core/option.ts";
import { Json } from "../../core/json.ts";
import type { NaiveDate } from "@tento-chrono";
import { DependencyAspect, ReferenceType } from "@tento-core/dependency-graph";
import { sheetInternals, sheetOf } from "../../sheet/sheet-stores.ts";

import type { CellEntry, CellValue } from "../../core/dtype.ts";
import { resolveRelativeLookupKey } from "../../ids/data-ref-resolve.ts";
import { ComputedDataSource, declareDependencyEdges, declareRecomputeMemo } from "./computed-data-source.ts";
import { SourceCells } from "./source-cells.ts";
import { SourceRow } from "./source-row.ts";

/**
 * One term of a multi-source `blend`: a cross-source `DataRef` (`source`,
 * absolute or relative; defaults to the row's primary `source` when omitted), a
 * dotted `field` into that source's obj cell, and a scalar `weight`.
 */
interface BlendTerm {
  readonly source?: string;
  readonly field: string;
  readonly weight: number;
}

/** Decode a `blend` list; entries without a string `field` and numeric `weight` are dropped. */
function decodeBlend(list: readonly Json[]): BlendTerm[] {
  const out: BlendTerm[] = [];
  for (const item of list) {
    const obj = Json.object(item);
    const field = Json.string(obj, "field");
    const weight = Json.number(obj, "weight");
    if (field == null || weight == null) continue;
    const source = Json.string(obj, "source");
    out.push({ field, weight, ...(source != null ? { source } : {}) });
  }
  return out;
}

export class DerivedDataSource extends ComputedDataSource {
  static {
    declareDependencyEdges(DerivedDataSource, (row) => row.#registerDependencyEdges());
    declareRecomputeMemo(DerivedDataSource, (row, range) => row.#dependencySignature(range));
  }

  /** Scalar multiplier applied to the projected value (default passthrough). */
  #factor(): number {
    return Json.number(this.attributes(), "factor") ?? 1;
  }

  /** Dotted path into an obj cell value to extract a scalar (`score.hrv_rmssd_milli`). */
  #field(): Option<string> {
    return Json.string(this.attributes(), "field");
  }

  /** `{dottedPath: weight}` map → the cell value is the weighted sum of those fields. */
  #weights(): Option<Record<string, number>> {
    return Json.numbers(this.attributes(), "weights");
  }

  /**
   * Multi-source blend: `[{ source?, field, weight }]`. Each term reads a
   * (possibly different) source's obj cell and contributes `field·weight`; the
   * row's own `source` is the date spine and may also contribute via `field` /
   * `weights`. Lets a composite — e.g. a health score across Whoop HRV + Garmin
   * steps — span multiple source rows by address.
   */
  #blend(): Option<readonly BlendTerm[]> {
    const list = Json.array(this.attributes(), "blend");
    return list == null ? null : decodeBlend(list);
  }

  /**
   * The raw `source` `DataRef` this row derives FROM (mirrors
   * `data-series.source`): a lookupKey naming another row. Absent ⇒ the in-tree
   * parent path.
   */
  #sourceRef(): Option<string> {
    return Json.string(this.attributes(), "source");
  }

  /**
   * Resolve a `source` `DataRef` to a row. **Absolute** when it names a key
   * (`:` / `[owner]:~` prefix) — used as-is. **Relative** otherwise — resolved
   * against this row's nearest keyed ancestor exactly like a `localPath` address,
   * so a derived row can read a SIBLING's output by path (e.g. `source: "due"`
   * under `:~lona:task` → `:~lona:task.due`), composing the same way addresses
   * do. `null` when the ref doesn't resolve to a `SourceRow` (a relative one with
   * no keyed ancestor, or an unknown key) — the row then materializes empty
   * rather than silently deriving from the wrong row.
   */
  /** Resolve a `source` `DataRef` to a concrete lookupKey: **absolute** as-is
   *  (`:`/`[owner]:~`), else **relative** against the nearest keyed ancestor.
   *  `undefined` when a relative ref has no keyed ancestor. */
  #keyForRef(ref: string): Option<string> {
    if (ref.startsWith(":") || ref.startsWith("[")) return ref;
    return resolveRelativeLookupKey(this, ref);
  }

  #resolveRowForRef(ref: string): Option<SourceRow> {
    const key = this.#keyForRef(ref);
    if (key == null) return null;
    const row = sheetOf(this).peekByLookupKey(key);
    return row instanceof SourceRow ? row : null;
  }

  /**
   * Register Derived→Source dependency edges into `sheet.graph` so the sheet's
   * `ColumnDataChanged` handler recomputes this row when any source's cells
   * change (`Sheet.enqueueDependentRecomputes`). Covers the primary `source`
   * (or the in-tree parent) plus every cross-source `blend` term, each keyed by
   * its resolved lookupKey (`LOOKUP_KEY`) so the edge is stable across late
   * source resolution. Idempotent — `addDependencyEdge` dedupes.
   */
  #registerDependencyEdges(): void {
    const graph = sheetInternals(sheetOf(this)).graph;
    const ref = this.#sourceRef();
    const primaryKey = ref != null ? this.#keyForRef(ref) : undefined;
    const add = (row: SourceRow, key: Option<string>): void => {
      graph.addDependencyEdge(this.id, {
        targetRowId: row.id,
        referenceType: key != null ? ReferenceType.LOOKUP_KEY : ReferenceType.ID,
        referenceLiteral: key ?? null,
        aspects: new Set([DependencyAspect.VALUES]),
      });
    };
    const primary = this.#sourceRow();
    if (primary != null) add(primary, primaryKey ?? null);
    for (const t of this.#blend() ?? []) {
      if (t.source != null) {
        const row = this.#resolveRowForRef(t.source);
        if (row != null) add(row, this.#keyForRef(t.source));
      } else if (primary != null) {
        add(primary, primaryKey ?? null);
      }
    }
  }

  /**
   * Memo signature for this row's external inputs (declared with
   * `declareRecomputeMemo`: the recompute is a pure overwrite, no destructive
   * pre-clear, so the memo is safe). Digests, in the exact order `computeCellsForRange` /
   * `computeBlend` read them, the resolved primary/spine source plus every
   * blend term's source: each row's identity (`row.id` — so a re-resolution to
   * a DIFFERENT row invalidates even when both rows' cells are unchanged) and
   * its cells in `range` (date + id + value — so any input-cell change
   * invalidates). An unresolved term contributes `null`, so a source that
   * becomes resolvable later also flips the signature. Reads the SAME cells the
   * compute does, but skips the per-cell projection + `set` fan-out — a strict,
   * cheap subset of a full recompute. Fully JSON: no delimiter can collide.
   */
  #dependencySignature(range: NaiveDate.Range): string {
    const primary = this.#sourceRow();
    const sources: Array<Option<SourceRow>> = [primary];
    for (const t of this.#blend() ?? []) {
      sources.push(t.source != null ? this.#resolveRowForRef(t.source) : primary);
    }
    return JSON.stringify(
      sources.map((row) =>
        row == null ? null : {
          row: row.id.toString(),
          cells: SourceCells.dates(row.peekCells(range)).map((c) => ({
            d: c.date.toString(),
            id: c.id ?? null,
            v: c.value ?? null,
          })),
        }
      ),
    );
  }

  /**
   * Resolve the row this derives from: the `source` `DataRef` (cross-row /
   * relative) when declared, else the parent (in-tree). A *declared* `source`
   * never falls back to the parent.
   */
  #sourceRow(): Option<SourceRow> {
    const ref = this.#sourceRef();
    if (ref != null) return this.#resolveRowForRef(ref);
    return this.parent instanceof SourceRow ? this.parent : null;
  }

  /**
   * Read the resolved source's cells for the range and project each one:
   * `field` extracts a scalar from an obj path, `weights` blends several obj
   * fields, else numeric cells pass through; the result is scaled by `factor`.
   * An obj cell with no extractable number is dropped (sparse series). `[]` when
   * the source does not resolve.
   */
  override computeCellsForRange(range: NaiveDate.Range): CellEntry[] {
    if (this.#blend() != null) return this.#computeBlend(range);
    const source = this.#sourceRow();
    if (source == null) return [];
    const factor = this.#factor();
    const field = this.#field();
    const weights = this.#weights();
    const out: CellEntry[] = [];
    for (const cell of SourceCells.dates(source.peekCells(range))) {
      if (field == null && weights == null) {
        // Passthrough: scale numeric cells, leave non-numeric untouched.
        const value = typeof cell.value === "number"
          ? cell.value * factor
          : cell.value;
        out.push({ ...cell, value });
        continue;
      }
      // Obj projection: extract a field or a weighted blend, then scale.
      const projected = field != null
        ? numAtPath(cell.value, field)
        : weights != null
        ? weightedSum(cell.value, weights)
        : null; // unreachable: passthrough took `field == null && weights == null`
      if (projected == null) continue;
      out.push({ ...cell, value: projected * factor });
    }
    return out;
  }

  /**
   * Multi-source blend. The row's primary `source` (or parent) is the **date
   * spine**: for each of its cells we sum the primary's own contribution
   * (`field` / `weights`, or a passthrough number) plus each cross-source `blend`
   * term (`field·weight` read from that term's source at the *same* date), scaled
   * by `factor`. A date present only in a secondary source is skipped — the spine
   * defines the series. `[]` when the spine does not resolve.
   */
  #computeBlend(range: NaiveDate.Range): CellEntry[] {
    const spine = this.#sourceRow();
    if (spine == null) return [];
    const factor = this.#factor();
    const field = this.#field();
    const weights = this.#weights();
    // Pre-index each cross-source term's cells by date key (exact-match lookup;
    // the spine's own iteration provides the chronological order).
    const terms = (this.#blend() ?? []).map((t) => {
      const row = t.source != null ? this.#resolveRowForRef(t.source) : spine;
      const byDate = new Map<string, CellValue>();
      if (row != null) {
        for (const cell of SourceCells.dates(row.peekCells(range))) {
          byDate.set(cell.date.toString(), cell.value);
        }
      }
      return { field: t.field, weight: t.weight, byDate };
    });
    const out: CellEntry[] = [];
    for (const cell of SourceCells.dates(spine.peekCells(range))) {
      let sum = 0;
      let any = false;
      // Primary contribution: an explicit field/weights projection, else a bare
      // numeric passthrough (an obj spine with neither contributes nothing).
      const own = field != null
        ? numAtPath(cell.value, field)
        : weights != null
        ? weightedSum(cell.value, weights)
        : (typeof cell.value === "number" ? cell.value : null);
      if (own != null) {
        sum += own;
        any = true;
      }
      const key = cell.date.toString();
      for (const term of terms) {
        const tv = term.byDate.get(key);
        const v = tv === undefined ? null : numAtPath(tv, term.field);
        if (v != null) {
          sum += v * term.weight;
          any = true;
        }
      }
      if (any) out.push({ ...cell, value: sum * factor });
    }
    return out;
  }
}

/** Walk a dotted path into an obj cell value; returns the leaf number, or `null`. */
function numAtPath(value: CellValue, path: string): Option<number> {
  let cur: unknown = value;
  for (const seg of path.split(".")) {
    if (cur == null || typeof cur !== "object") return null;
    cur = (cur as Record<string, unknown>)[seg];
  }
  return typeof cur === "number" ? cur : null;
}

/** Weighted sum of the named (dotted-path) fields; `null` when none are present. */
function weightedSum(
  value: CellValue,
  weights: Record<string, number>,
): Option<number> {
  let sum = 0;
  let any = false;
  for (const [path, w] of Object.entries(weights)) {
    const v = numAtPath(value, path);
    if (v != null) {
      sum += v * w;
      any = true;
    }
  }
  return any ? sum : null;
}
